/**
 * Firestore Adapter
 * Automatically handles Firestore integration
 */

const defaultFirebaseConfig = {
  apiKey: 'AIzaSyCFNW8gZsd4QP1Cyy056Bu6NDJ6HhViFJE',
  authDomain: 'seo-metadata-f79a0.firebaseapp.com',
  databaseURL: 'https://seo-metadata-f79a0-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: 'seo-metadata-f79a0',
  storageBucket: 'seo-metadata-f79a0.firebasestorage.app',
  messagingSenderId: '402670906477',
  appId: '1:402670906477:web:885e295253f852e10fe364',
  measurementId: 'G-J48G8WXE2B',
};

class FirestoreAdapter {
  constructor(firebaseConfig = defaultFirebaseConfig, options = {}) {
    this.firebaseConfig = firebaseConfig;
    this.options = {
      collection: options.collection || 'seo_data',
      batchWrites: options.batchWrites !== false, // Use batch writes for efficiency
      ...options,
    };

    this.db = null;
    this.firestore = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Firebase and Firestore
   */
  async init() {
    if (this.isInitialized) return;

    try {
      // Dynamically import Firebase (only when needed)
      const { initializeApp } = await import('firebase/app');
      const { getFirestore, collection, addDoc, writeBatch, doc } = await import(
        'firebase/firestore'
      );

      // Initialize Firebase
      const app = initializeApp(this.firebaseConfig);
      this.db = getFirestore(app);
      this.firestore = { collection, addDoc, writeBatch, doc };

      this.isInitialized = true;
      console.log('[Firestore Adapter] ✅ Initialized successfully');
    } catch (error) {
      console.error('[Firestore Adapter] ❌ Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Store data in Firestore
   */
  async store(payload) {
    if (!this.isInitialized) {
      await this.init();
    }

    console.log(`[Firestore Adapter] 💾 Storing ${payload.events.length} events...`);

    try {
      if (this.options.batchWrites && payload.events.length > 1) {
        // Use batch write for multiple events (more efficient)
        await this.batchWrite(payload.events);
      } else {
        // Single writes
        for (const event of payload.events) {
          await this.writeDocument(event);
        }
      }

      console.log('[Firestore Adapter] ✅ Data stored successfully');
      return { success: true };
    } catch (error) {
      console.error('[Firestore Adapter] ❌ Storage failed:', error);
      throw error;
    }
  }

  /**
   * Write single document
   */
  async writeDocument(event) {
    const docData = this.formatDocument(event);

    const docRef = await this.firestore.addDoc(
      this.firestore.collection(this.db, this.options.collection),
      docData,
    );

    console.log('[Firestore Adapter] Document written:', docRef.id);
    return docRef;
  }

  /**
   * Batch write multiple documents
   */
  async batchWrite(events) {
    const batch = this.firestore.writeBatch(this.db);

    for (const event of events) {
      const docData = this.formatDocument(event);
      const docRef = this.firestore.doc(
        this.firestore.collection(this.db, this.options.collection),
      );
      batch.set(docRef, docData);
    }

    await batch.commit();
    console.log(`[Firestore Adapter] Batch write completed: ${events.length} documents`);
  }

  /**
   * Format document for Firestore
   */
  formatDocument(event) {
    return {
      sessionId: event.sessionId,
      timestamp: Date.now(),

      // Page data
      page: {
        url: event.page?.url,
        title: event.page?.title,
        description: event.page?.description,
        wordCount: event.page?.wordCount,
        headings: event.page?.headings,
      },

      // SEO analysis (optimized - top items only)
      seo: {
        keywords: event.keywords?.slice(0, 10), // Top 10 keywords
        contentQuality: event.contentQuality,
        issuesCount: event.issues?.length || 0,
        topIssues: event.issues?.slice(0, 5)?.map(issue => ({
          type: issue.type,
          message: issue.message,
        })),
      },

      // Performance metrics
      performance: {
        loadTime: event.performance?.loadTime,
        fcp: event.performance?.fcp,
        lcp: event.performance?.lcp,
        cls: event.performance?.cls,
        fid: event.performance?.fid,
      },

      // Engagement summary (aggregated)
      engagement: {
        clicks: event.engagement?.clicks?.length || 0,
        scrolls: event.engagement?.scrollEvents?.length || 0,
        maxScrollDepth: event.engagement?.maxScrollDepth || 0,
        timeOnPage: event.engagement?.totalTimeOnPage || 0,
      },

      // Additional data
      serpFeatures: event.serpFeatures,
      backlinks: {
        internal: event.backlinks?.internal?.length || 0,
        external: event.backlinks?.external?.length || 0,
      },
    };
  }
}

export default FirestoreAdapter;
